import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, ShoppingBag, Factory, ArrowRight, Lock, Mail, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Turnstile from "@/components/Turnstile";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href =
    `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleButton({ testId = "google-signin-btn", label = "Continue with Google" }) {
  return (
    <button
      type="button"
      onClick={startGoogleLogin}
      data-testid={testId}
      className="w-full flex items-center justify-center gap-3 border border-[#E2E8F0] bg-white text-[#1C3E5F] px-4 py-3 hover:border-[#B8965A] hover:bg-[#FBF7EE] transition-colors font-medium"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
      <span>{label}</span>
    </button>
  );
}

const TEAM_ROLES = [
  "super_admin", "admin", "management", "finance",
  "sales", "procurement", "qc", "operations", "partner",
];

const PORTALS = [
  {
    code: "team",
    name: "Team / Internal",
    desc: "Sales, procurement, QC, management & administration access to the internal CRM, Kanban, supplier database, finance and CMS.",
    icon: Briefcase,
    roles: TEAM_ROLES,
    register: null, // team accounts are provisioned by admin
    register_label: "Team accounts are provisioned by admin",
  },
  {
    code: "client",
    name: "Client / Buyer",
    desc: "Importers and buyers running RFQs and active sourcing projects with Fortune. Track orders, view QC, download docs.",
    icon: ShoppingBag,
    roles: ["client"],
    register: "/register",
    register_label: "Create a client account",
  },
  {
    code: "vendor",
    name: "Vendor / Supplier",
    desc: "Chinese factories and supplier partners who quote on RFQs, share production updates, and manage compliance documents.",
    icon: Factory,
    roles: ["supplier", "vendor"],
    register: "/suppliers/register",
    register_label: "Register your factory",
  },
];

function _correctPortalFor(role) {
  if (TEAM_ROLES.includes(role)) return "Team";
  if (role === "supplier" || role === "vendor") return "Vendor";
  return "Client";
}

function _landingFor(role) {
  if (TEAM_ROLES.includes(role)) return "/portal/home";
  if (role === "supplier" || role === "vendor") return "/portal/vendor";
  return "/portal/client";
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initial = params.get("as");
  const [portal, setPortal] = useState(
    PORTALS.find((p) => p.code === initial) ? initial : null,
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tsToken, setTsToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password, tsToken);

      // Validate portal-mismatch, block sign-in via wrong portal door
      const sel = PORTALS.find((p) => p.code === portal);
      if (sel && !sel.roles.includes(u.role)) {
        toast.error(
          `This account is a "${u.role}" account. Use the ${_correctPortalFor(u.role)} login instead.`,
        );
        setBusy(false);
        return;
      }

      toast.success(`Welcome, ${u.name}`);
      nav(_landingFor(u.role));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Step 1, choose portal                                              */
  /* ------------------------------------------------------------------ */
  if (!portal) {
    return (
      <div data-testid="login-chooser" className="min-h-[80vh] bg-[#12283F] text-white py-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow text-[#B8965A] mb-3">Sign in</div>
            <h1 className="font-serif text-5xl lg:text-6xl leading-tight">Choose your login.</h1>
            <p className="text-white/60 text-lg mt-4 max-w-2xl mx-auto">
              Three doors, same operating system. Pick the one that matches your relationship with Fortune Sourcings.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PORTALS.map((p) => (
              <button
                key={p.code}
                onClick={() => setPortal(p.code)}
                data-testid={`portal-${p.code}`}
                className="text-left bg-[#1C3E5F] border border-[#B8965A]/20 hover:border-[#B8965A] p-7 group transition-all"
              >
                <div className="w-12 h-12 mb-5 border border-[#B8965A]/40 flex items-center justify-center text-[#B8965A] group-hover:bg-[#B8965A] group-hover:text-[#12283F] transition-colors">
                  <p.icon size={20} />
                </div>
                <div className="eyebrow text-[#B8965A] mb-2">{p.name}</div>
                <h3 className="font-serif text-2xl leading-tight mb-3">Login as {p.code}</h3>
                <p className="text-white/65 text-sm leading-relaxed mb-5">{p.desc}</p>
                <div className="inline-flex items-center gap-2 text-[#B8965A] text-[11px] uppercase tracking-[0.16em] font-semibold">
                  Continue <ArrowRight size={12} />
                </div>
              </button>
            ))}
          </div>

          <div className="mt-10 text-center text-white/55 text-sm">
            New here? <Link to="/register" className="text-[#B8965A] hover:underline">Create a client account</Link>
            {" · "}
            <Link to="/suppliers/register" className="text-[#B8965A] hover:underline">Register your factory</Link>
          </div>

          <div className="mt-10 max-w-sm mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-white/15" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/50">or</span>
              <div className="flex-1 h-px bg-white/15" />
            </div>
            <GoogleButton testId="google-signin-chooser" />
            <p className="text-white/45 text-[11px] mt-3 text-center leading-relaxed">
              Sign in with Google if you already have an account, or create a client account on the fly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Step 2, sign-in form                                               */
  /* ------------------------------------------------------------------ */
  const sel = PORTALS.find((p) => p.code === portal);

  return (
    <div data-testid="login-page" className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#1C3E5F]">
        <img src="https://images.unsplash.com/photo-1765206257996-9b4a5d886a2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxjb250YWluZXIlMjBwb3J0JTIwc2hpcCUyMGxvZ2lzdGljc3xlbnwwfHx8fDE3Nzc2NTI5Njl8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="eyebrow text-[#B8965A] mb-3">Fortune Sourcings, {sel.name} Portal</div>
          <h2 className="font-serif text-5xl leading-tight">Your sourcing operation, in one place.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-md">
          <button
            type="button"
            onClick={() => setPortal(null)}
            className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#1C3E5F] hover:text-[#B8965A] mb-4"
            data-testid="login-change-portal"
          >
            ← Change portal
          </button>
          <div className="eyebrow text-[#B8965A] mb-3">{sel.name} sign in</div>
          <h1 className="font-serif text-[#1C3E5F] text-4xl mb-8">Welcome back.</h1>

          <div className="mb-6">
            <GoogleButton testId="google-signin-form" />
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[#E2E8F0]" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280]">or continue with email</span>
              <div className="flex-1 h-px bg-[#E2E8F0]" />
            </div>
          </div>

          <div className="space-y-5">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1C3E5F] flex items-center gap-2 mb-2"><Mail size={11} className="text-[#B8965A]" /> Email</span>
              <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-[#E2E8F0] px-4 py-3 focus:border-[#B8965A] outline-none" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1C3E5F] flex items-center gap-2 mb-2"><Lock size={11} className="text-[#B8965A]" /> Password</span>
              <div className="relative">
                <input data-testid="login-password" required type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-[#E2E8F0] px-4 py-3 focus:border-[#B8965A] outline-none pr-14" />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="login-show-password"
                  className="absolute right-0 top-0 h-full w-12 flex items-center justify-center text-[#6B7280] hover:text-[#B8965A] active:text-[#B8965A] transition-colors" aria-label={showPw ? "Hide password" : "Show password"}>
                  {showPw ? <EyeOff size={20}/> : <Eye size={20}/>}
                </button>
              </div>
            </label>
            <div className="text-right -mt-1">
              <Link to="/forgot-password" data-testid="login-forgot-link"
                className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] hover:text-[#12283F]">Forgot password?</Link>
            </div>
            <Turnstile action="login" onVerify={setTsToken} />
            <button data-testid="login-submit" disabled={busy} className="btn-dark w-full">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </div>

          <div className="mt-8 text-sm text-[#6B7280] space-y-1">
            {sel.register ? (
              <div>{sel.register_label}? <Link to={sel.register} className="text-[#1C3E5F] font-semibold link-underline">{sel.register_label.toLowerCase().includes("create") ? "Create" : "Register"}</Link></div>
            ) : (
              <div className="italic">{sel.register_label}.</div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
